import { useDeferredValue, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type PricingPlanQueryV4Row,
  useGetV4PricingPlanReportByPlan,
} from "../api/pricing_plan/v4PricingPlanReportByPlan";
import { css } from "../../../../styled-system/css";
import { type ColumnDef, getCoreRowModel, getExpandedRowModel, useReactTable } from "@tanstack/react-table";
import { type ManagedTableState, type ManagedTableStateSetters } from "components/table/usePersistedTableState";
import { HStack, VStack, Wrap } from "../../../../styled-system/jsx";
import { RT8ColumnSelector } from "components/core/blocks/MetricSelector";
import { SearchInput } from "components/fields/base/TextField";
import { RT8Pagination } from "components/table/Pagination";
import { Table } from "components/table/Table";
import { resetTable } from "components/table/utils";
import { RT8FilterWidget } from "views/merchandise/tables/FilterWidget";
import { Button } from "components/core/basic/Button";
import { MdRotateLeft } from "react-icons/md";
import { useCategoriesQuery } from "hooks/useCategories";
import { ENROLL_OPTIONS } from "views/advertising/productLifecycleFilters";
import PricingPlanCard from "modules/pricingv2/PricingPlanCard";
import { useGetV4PricingPlanReportOptions } from "../api/pricing_plan/v4PricingPlanReportOptions";

const PricingPlanReportTable = ({
  columns,
  state,
  initialState,
  setters,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<PricingPlanQueryV4Row, any>[];
  state: ManagedTableState;
  initialState: ManagedTableState;
  setters: ManagedTableStateSetters;
}) => {
  const deferredSearch = useDeferredValue(state.globalFilter);
  const queryClient = useQueryClient();

  const byPlanOptionsGetter = useGetV4PricingPlanReportByPlan();
  const pricingPlanByPlanReportQuery = useQuery(
    byPlanOptionsGetter({
      pagination: state.pagination,
      sorting: state.sorting,
      filters: state.columnFilters,
      search: deferredSearch,
    }),
  );
  const productReportOptionsQuery = useQuery(useGetV4PricingPlanReportOptions());

  void queryClient.prefetchQuery({
    ...byPlanOptionsGetter({
      pagination: { pageIndex: state.pagination.pageIndex + 1, pageSize: state.pagination.pageSize },
      sorting: state.sorting,
      filters: state.columnFilters,
      search: deferredSearch,
    }),
    staleTime: 10000,
  });

  const categoriesQuery = useCategoriesQuery();

  const tagOptions = useMemo(
    () => [
      { value: "all", label: "All Tags" },
      ...(productReportOptionsQuery.data?.data.tags.map((tag) => {
        return {
          value: tag,
          label: tag,
        };
      }) ?? []),
    ],
    [productReportOptionsQuery.data?.data.tags],
  );

  const categoryOptions = useMemo(
    () => [
      { value: "all", label: "All Categories" },
      ...(categoriesQuery.data?.map((category) => {
        return {
          value: category.id.toString(),
          label: category.leaf_category_name,
        };
      }) ?? []),
    ],
    [categoriesQuery.data],
  );
  const table = useReactTable({
    data: pricingPlanByPlanReportQuery.data?.data ?? [],
    columns,
    defaultColumn: {
      filterFn: () => true,
    },
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => true,
    getRowId: (row) => row.pricing_plan_id.toString(),
    enableRowSelection: true,
    rowCount: pricingPlanByPlanReportQuery.data?.count || 0,
    initialState: initialState,
    state: state,
    ...setters,
    manualPagination: true,
    meta: {
      showSkeleton:
        pricingPlanByPlanReportQuery.isFetching &&
        (!pricingPlanByPlanReportQuery.data || pricingPlanByPlanReportQuery.isPlaceholderData),
      columnFilterSelectOptions: {
        category_id: categoryOptions,
        tags: tagOptions,
        pricing_plan_status: ENROLL_OPTIONS,
      },
    },
  });

  // const selected = selectionStateToSelectedIds(table.getState().rowSelection);

  if (pricingPlanByPlanReportQuery.isError) {
    return (
      <div>
        Error loading product report. {import.meta.env.PROD ? null : JSON.stringify(pricingPlanByPlanReportQuery.error)}
      </div>
    );
  }

  return (
    <div>
      <Wrap justifyContent={"end"} width={"100%"} padding={"small"} fontSize={"large"}>
        {/* <BulkActionButton selected={selected} options={[]} /> */}
      </Wrap>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              onPress={() => {
                resetTable(table);
              }}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={"Search"}
              isExpandable
              className={css({ flex: "0 1 350px" })}
              value={table.getState().globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table
        table={table}
        className={css({ minWidth: "100%" })}
        renderExpander={(row) => {
          return (
            <div
              className={css({
                width: "100%",
                padding: "large",
                backgroundColor: "#efefef",
              })}
            >
              <PricingPlanCard planId={row.original.pricing_plan_id} />
            </div>
          );
        }}
      />
      {/* <HStack width={"100%"} padding={"small"}>
        <DownloadV4Report downloadMutation={exportMutation} />
      </HStack> */}
    </div>
  );
};

export default PricingPlanReportTable;
